import { Controller } from "@hotwired/stimulus";
import { smartMatch } from "../../utils/smart_search";

export default class extends Controller {
  static targets = [
    "scopeRadio",
    "oneStudentFilter",
    "multipleStudentsFilter",
    "oneClassFilter",
    "filterZone",
    "multipleClassesFilter",
  ];

  connect() {
    // Load translations using the new translation system
    this.loadTranslations();

    this.updateFilters();
    this.scopeRadioTargets.forEach((radio) => {
      radio.addEventListener("change", () => {
        this.updateFilters();
        this.hideError(); // Hide any previous errors when changing scope
      });
    });

    // Student search (single student)
    const searchInput = this.element.querySelector("#student-search-input");
    if (searchInput) {
      // Set placeholder from translations
      searchInput.placeholder = this.t("placeholders.search_student");
      searchInput.addEventListener("input", (e) => this.filterStudents(e));
    }

    // Single student selection from list
    this.element
      .querySelectorAll("#search-results .cursor-pointer")
      .forEach((el) => {
        el.addEventListener("click", (e) => this.selectStudent(e, el));
      });

    // Multiple students management
    this.setupMultipleStudents();

    // Generation form management
    const form = document.getElementById("generation-form");
    if (form) {
      form.addEventListener("submit", (e) => {
        if (!this.prepareFormSubmission()) {
          e.preventDefault();
          return false;
        }
      });
    }
  }

  /**
   * Load translations for this controller
   */
  loadTranslations() {
    try {
      // Try to get translations from data attributes first
      const translationsData = this.element.dataset.translations;
      if (translationsData) {
        const data = JSON.parse(translationsData);
        this.translations = data;
        return;
      }

      // Try to get translations from script tag
      const scriptTag = document.getElementById(
        "school-certificates-translations",
      );
      if (scriptTag) {
        this.translations = JSON.parse(scriptTag.textContent);
        return;
      }

      // Fallback to empty object if no translations found
      this.translations = {};

      // Log warning in development
      if (this.isDevelopment()) {
        console.warn(
          "No translations found for school certificates controller",
        );
      }
    } catch (error) {
      console.error(
        "Error loading translations in school certificates controller:",
        error,
      );
      this.translations = {};
    }
  }

  /**
   * Check if we're in development environment
   */
  isDevelopment() {
    return (
      document.body.dataset.environment === "development" ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    );
  }

  /**
   * Get translated text with interpolation support
   */
  t(key, options = {}) {
    const keys = key.split(".");
    let value = this.translations;

    // Navigate through nested keys
    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        // Return key if translation not found
        return `[${key}]`;
      }
    }

    // Handle pluralization
    if (typeof value === "object" && "one" in value && "other" in value) {
      const count = options.count || 0;
      value = count === 1 ? value.one : value.other;
    }

    // Handle interpolation
    if (typeof value === "string" && options) {
      Object.keys(options).forEach((optionKey) => {
        if (optionKey !== "count") {
          value = value.replace(
            new RegExp(`%{${optionKey}}`, "g"),
            options[optionKey],
          );
        }
      });
    }

    return value || `[${key}]`;
  }

  updateFilters() {
    const selected = this.scopeRadioTargets.find(
      (radio) => radio.checked,
    )?.value;
    this.oneStudentFilterTarget.classList.toggle(
      "hidden",
      selected !== "one_student",
    );
    this.multipleStudentsFilterTarget.classList.toggle(
      "hidden",
      selected !== "multiple_students",
    );
    this.oneClassFilterTarget.classList.toggle(
      "hidden",
      selected !== "one_class",
    );
    if (this.hasMultipleClassesFilterTarget) {
      this.multipleClassesFilterTarget.classList.toggle(
        "hidden",
        selected !== "multiple_classes",
      );
    }
    if (selected === "multiple_students") {
      this.setupMultipleStudents();
    }
    if (selected === "multiple_classes") {
      this.setupMultipleClasses();
    }
    if (selected === "one_class") {
      this.setupOneClass();
    }
  }

  filterStudents(e) {
    const query = e.target.value;
    const results = this.element.querySelectorAll(
      "#search-results .cursor-pointer",
    );
    results.forEach((el) => {
      const name = el.querySelector(".font-medium").textContent;
      const matricule = el.querySelector(".text-gray-500").textContent;
      el.style.display = smartMatch([name, matricule], query) ? "" : "none";
    });
  }

  selectStudent(e, el) {
    // Retire la sélection précédente
    this.element
      .querySelectorAll("#search-results .cursor-pointer")
      .forEach((div) => {
        div.classList.remove("border-2", "border-cta", "bg-cta-shared");
      });
    // Ajoute la sélection visuelle
    el.classList.add("border-2", "border-cta", "bg-cta-shared");
    // Tu peux ici stocker l'ID de l'élève sélectionné dans un champ caché si besoin
  }

  setupMultipleStudents() {
    // Management of counting and filtering for multiple selection
    const table = this.element.querySelector("#multiple-students-filter table");
    if (!table) return;
    const checkboxes = table.querySelectorAll(".student-checkbox");
    const selectAll = table.querySelector("#select-all");
    const countSpan = this.element.querySelector("#selected-count");

    // Initialize count
    this.updateSelectedCount(checkboxes, countSpan);

    // Listener on each checkbox
    checkboxes.forEach((cb) => {
      cb.addEventListener("change", () =>
        this.updateSelectedCount(checkboxes, countSpan),
      );
    });

    // Select all
    if (selectAll) {
      selectAll.addEventListener("change", (e) => {
        checkboxes.forEach((cb) => {
          cb.checked = selectAll.checked;
        });
        this.updateSelectedCount(checkboxes, countSpan);
      });
    }

    // Filtering
    const filterInputs = this.element.querySelectorAll(
      '#multiple-students-filter select, #multiple-students-filter input[type="checkbox"]',
    );
    filterInputs.forEach((input) => {
      input.addEventListener("change", () => this.filterMultipleStudents());
    });
    const searchInput = this.element.querySelector(
      '#multiple-students-filter input[type="search"]',
    );
    if (searchInput) {
      // Set placeholder from translations
      searchInput.placeholder = this.t("placeholders.search_student");
      searchInput.addEventListener("input", () =>
        this.filterMultipleStudents(),
      );
    }
  }

  updateSelectedCount(checkboxes, countSpan) {
    const count = Array.from(checkboxes).filter((cb) => cb.checked).length;
    if (countSpan) {
      countSpan.textContent = this.t("counters.students_selected", { count });
    }
  }

  filterMultipleStudents() {
    const table = this.element.querySelector("#multiple-students-filter table");
    if (!table) return;
    const classFilter = this.element.querySelector(
      "#multiple-students-filter select:nth-of-type(1)",
    )?.value;
    const statusFilter = this.element.querySelector(
      "#multiple-students-filter select:nth-of-type(2)",
    )?.value;
    const includeInactive = this.element.querySelector(
      '#multiple-students-filter input[type="checkbox"]',
    )?.checked;
    const rows = table.querySelectorAll("tbody tr");
    rows.forEach((row) => {
      const className = row
        .querySelector("td:nth-child(3)")
        ?.textContent.trim();
      const status = row
        .querySelector("td:nth-child(4) span")
        ?.textContent.trim();
      let show = true;
      if (
        classFilter &&
        classFilter !== "Toutes les classes" &&
        className !== classFilter
      )
        show = false;
      if (statusFilter === "Élèves actifs" && status !== "Actif") show = false;
      if (statusFilter === "Élèves inactifs" && status !== "Inactif")
        show = false;
      if (!includeInactive && status === "Inactif") show = false;
      row.style.display = show ? "" : "none";
    });
  }

  setupMultipleClasses() {
    const table = this.element.querySelector("#multiple-classes-filter table");
    if (!table) return;
    const checkboxes = table.querySelectorAll(".class-checkbox");
    const selectAll = table.querySelector("#select-all-classes");
    const countSpan = this.element.querySelector("#selected-classes-count");

    // Initialize count
    this.updateSelectedClassesCount(checkboxes, countSpan);

    // Listener on each checkbox
    checkboxes.forEach((cb) => {
      cb.addEventListener("change", () =>
        this.updateSelectedClassesCount(checkboxes, countSpan),
      );
    });

    // Select all
    if (selectAll) {
      selectAll.addEventListener("change", (e) => {
        checkboxes.forEach((cb) => {
          cb.checked = selectAll.checked;
        });
        this.updateSelectedClassesCount(checkboxes, countSpan);
      });
    }

    // Filters
    const levelFilter = this.element.querySelector("#class-level-filter");
    const minStudentsFilter = this.element.querySelector(
      "#class-min-students-filter",
    );
    if (levelFilter) {
      levelFilter.addEventListener("change", () =>
        this.filterMultipleClasses(),
      );
    }
    if (minStudentsFilter) {
      // Set placeholder from translations
      minStudentsFilter.placeholder =
        this.t("placeholders.min_students") || "Min. students";
      minStudentsFilter.addEventListener("input", () =>
        this.filterMultipleClasses(),
      );
    }
  }

  updateSelectedClassesCount(checkboxes, countSpan) {
    const count = Array.from(checkboxes).filter((cb) => cb.checked).length;
    if (countSpan) {
      countSpan.textContent = this.t("counters.classes_selected", { count });
    }
  }

  filterMultipleClasses() {
    const table = this.element.querySelector("#multiple-classes-filter table");
    if (!table) return;
    const levelFilter = this.element.querySelector(
      "#class-level-filter",
    )?.value;
    const minStudents = parseInt(
      this.element.querySelector("#class-min-students-filter")?.value || "0",
      10,
    );
    const rows = table.querySelectorAll("tbody tr");
    rows.forEach((row) => {
      const levelId = row
        .querySelector("td:nth-child(3)")
        ?.getAttribute("data-level-id");
      const nbStudents = parseInt(
        row.querySelector("td:nth-child(4)")?.textContent.trim() || "0",
        10,
      );
      let show = true;
      if (
        levelFilter &&
        levelFilter !== "Tous les niveaux" &&
        levelId !== levelFilter
      )
        show = false;
      if (!isNaN(minStudents) && nbStudents < minStudents) show = false;
      row.style.display = show ? "" : "none";
    });
  }

  setupOneClass() {
    const niveauSelect = this.element.querySelector("#one-class-niveau-select");
    const classeSelect = this.element.querySelector("#one-class-classe-select");
    const previewDiv = this.element.querySelector("#one-class-preview");
    if (!niveauSelect || !classeSelect || !previewDiv) return;

    // Default message
    previewDiv.textContent = this.t("placeholders.select_class");

    // Filter classes by level
    niveauSelect.addEventListener("change", () => {
      const selectedNiveau = niveauSelect.value;
      Array.from(classeSelect.options).forEach((option) => {
        if (option.value === "") return;
        const optionNiveau = option.getAttribute("data-niveau-id");
        option.style.display =
          selectedNiveau === "" || optionNiveau === selectedNiveau
            ? ""
            : "none";
      });
      classeSelect.value = "";
      previewDiv.textContent = this.t("placeholders.select_class");
    });

    // Dynamic preview
    classeSelect.addEventListener("change", () => {
      const selectedOption = classeSelect.selectedOptions[0];
      if (!selectedOption || selectedOption.value === "") {
        previewDiv.textContent = this.t("placeholders.select_class");
        return;
      }
      const nbEleves = selectedOption.getAttribute("data-nb-eleves");
      const studentsText = this.t("counters.students");
      previewDiv.innerHTML = `<span class='font-semibold'>${selectedOption.textContent}</span> — <span class='text-blue-900'>${nbEleves} ${studentsText}</span>`;
    });
  }

  prepareFormSubmission() {
    // Validate selection first
    if (!this.validateSelection()) {
      return false;
    }

    // Scope
    const selectedScope = this.scopeRadioTargets.find(
      (radio) => radio.checked,
    )?.value;
    document.getElementById("scope-input").value = selectedScope;

    // Selection based on scope
    let selection = {};
    let summary = "";

    if (selectedScope === "one_student") {
      // Find selected student
      const selected = document.querySelector("#search-results .border-cta");
      if (selected) {
        selection.student_id = selected.getAttribute("data-student-id");
        const name = selected.querySelector(".font-medium")?.textContent;
        const classe = selected.querySelector(
          ".text-sm.text-gray-500",
        )?.textContent;
        summary = name
          ? `${name}${classe ? " (" + classe.split("•")[0].trim() + ")" : ""}`
          : "";
      }
    } else if (selectedScope === "multiple_students") {
      // IDs of checked students
      const checked = Array.from(
        document.querySelectorAll(".student-checkbox:checked"),
      );
      selection.student_ids = checked.map((cb) => cb.value);
      const manualSelectionText = this.t("labels.manual_selection");
      const studentsText = this.t("counters.students");
      summary =
        checked.length > 0
          ? `${manualSelectionText} (${checked.length} ${studentsText})`
          : "";
    } else if (selectedScope === "one_class") {
      // ID of selected class
      const classeSelect = document.getElementById("one-class-classe-select");
      const selectedOption = classeSelect?.selectedOptions[0];
      selection.classroom_id = classeSelect?.value;
      if (selectedOption && selectedOption.value) {
        const label = selectedOption.textContent;
        summary = label;
      }
    } else if (selectedScope === "multiple_classes") {
      // IDs of checked classes
      const checked = Array.from(
        document.querySelectorAll(
          "#multiple-classes-filter .class-checkbox:checked",
        ),
      );
      selection.classroom_ids = checked.map((cb) => cb.value);
      if (checked.length > 0) {
        const labels = checked
          .map(
            (cb) =>
              cb.closest("tr").querySelector("td:nth-child(2)")?.textContent,
          )
          .filter(Boolean);
        const total = checked.reduce(
          (sum, cb) =>
            sum +
            parseInt(
              cb.closest("tr").querySelector("td:nth-child(4)")?.textContent ||
                "0",
              10,
            ),
          0,
        );
        const studentsText = this.t("counters.students");
        summary = `${labels.join(", ")} (${total} ${studentsText})`;
      }
    } else if (selectedScope === "whole_school") {
      // Count all students
      const total = document.querySelectorAll(".student-checkbox").length;
      const studentsText = this.t("counters.students");
      summary = `${total} ${studentsText}`;
      selection.whole_school = true;
    }

    selection.summary = summary;
    document.getElementById("selection-params-input").value =
      JSON.stringify(selection);

    return true;
  }

  /**
   * Validate the current selection
   */
  validateSelection() {
    const selectedScope = this.scopeRadioTargets.find(
      (radio) => radio.checked,
    )?.value;

    if (!selectedScope) {
      this.showError(this.t("errors.invalid_scope"));
      return false;
    }

    if (selectedScope === "one_student") {
      const selected = document.querySelector("#search-results .border-cta");
      if (!selected) {
        this.showError(this.t("errors.no_selection"));
        return false;
      }
    } else if (selectedScope === "multiple_students") {
      const checked = document.querySelectorAll(".student-checkbox:checked");
      if (checked.length === 0) {
        this.showError(this.t("errors.no_selection"));
        return false;
      }
    } else if (selectedScope === "one_class") {
      const classeSelect = document.getElementById("one-class-classe-select");
      if (!classeSelect?.value) {
        this.showError(this.t("errors.no_selection"));
        return false;
      }
    } else if (selectedScope === "multiple_classes") {
      const checked = document.querySelectorAll(
        "#multiple-classes-filter .class-checkbox:checked",
      );
      if (checked.length === 0) {
        this.showError(this.t("errors.no_selection"));
        return false;
      }
    }

    return true;
  }

  /**
   * Show error message to user
   */
  showError(message) {
    // Create or update error message element
    let errorElement = document.getElementById("selection-error");
    if (!errorElement) {
      errorElement = document.createElement("div");
      errorElement.id = "selection-error";
      errorElement.className =
        "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4";

      // Insert before the form
      const form = document.getElementById("generation-form");
      form.parentNode.insertBefore(errorElement, form);
    }

    errorElement.textContent = message;
    errorElement.style.display = "block";

    // Auto-hide after 5 seconds
    setTimeout(() => {
      if (errorElement) {
        errorElement.style.display = "none";
      }
    }, 5000);
  }

  /**
   * Hide error message
   */
  hideError() {
    const errorElement = document.getElementById("selection-error");
    if (errorElement) {
      errorElement.style.display = "none";
    }
  }
}
